import { FrameFeedback, Page } from './types';
import { assetIds, replaceAssets } from 'features/report-files/assetReferences';
import { visualRuntime } from './visualRuntime';
import { interactionChrome } from './interactionChrome';
import { imageDiagnostics } from './imageDiagnostics';

// Keep the iframe program as source so the app build cannot inject Babel helpers into it.
const runtimeSource = String.raw`
function runtime(token, visuals, dataKeys, assetErrors, assetURLs, canSaveAtLoad, fieldTexts) {
    let results = {};
    let editing = false;
    let loaded = false;
    let revision = null;
    let contextKey = null;
    let inspecting = false;
    let themeChoice = null;
    let interaction = null;
    let generation = 0;
    let requestId = 0;
    const requests = new Map();
    const themeListeners = new Set();
    const cleanups = new Map();
    const mounted = new WeakSet();
    let errors = [];
    const pageErrors = assetErrors || [];
    const assetImages = new Map();
    let callbacks = [];
    let selection = null;
    let selectedElement = null;
    let busySlots = [];
    let chromeFrame = 0;
    // Page-script saves (docs/architecture/live-html-pages.md section 8): the frame only asks; the host validates, calls the server and answers.
    let saveId = 0;
    const saves = new Map();
    // A remote text patch never overwrites the editable element a person is typing in (contenteditable or a textarea): it waits for
    // the focus to leave. A textarea shows its value; its text content is only its default once typed in.
    const deferredText = new Map();
    const edited = new WeakSet();
    const typing = node => node === document.activeElement && (node.isContentEditable || node.localName === 'textarea');
    const shown = node => node.localName === 'textarea' ? node.value : node.textContent;
    const show = (node, text) => {
        if (node.textContent !== text) node.textContent = text;
        if (node.localName === 'textarea' && node.value !== text) node.value = text;
    };
    // Each save-enabled field's text as this frame last applied it, with the page version it is from (as served, patched, a held
    // text shown, or saved): a save's previousText. A held-back patch doesn't change it, so a save over someone else's change
    // answers changed; older news never replaces newer.
    const appliedText = new Map(Object.entries(fieldTexts || {}).map(([uid, text]) => [uid, { text, version: 0 }]));
    const learn = (uid, text, version) => { if (!(appliedText.get(uid)?.version > version)) appliedText.set(uid, { text, version }); };
    const showHeld = node => {
        const held = deferredText.get(node);
        deferredText.delete(node); show(node, held.text); learn(node.getAttribute('data-unity-id'), held.text, held.version);
    };
    // A field's saves go one at a time, so each carries the text the one before it saved. A changed refusal also refuses the saves
    // queued behind it: they were typed over the refused text.
    const fieldSaves = new Map();
    const refusals = new Map();
    const send = (type, payload = {}) => window.parent.postMessage({ channel: 'unity-html', token, type, ...payload }, '*');
    ${interactionChrome}
    const reportError = (error, id, phase = 'render', persistent = false, location = {}) => {
        id = id || String(error?.stack || '').match(/unity-visual-([A-Za-z0-9_-]+)\.js/)?.[1];
        const message = String(error?.message || error || 'Rendering error').slice(0, 1500);
        if (persistent && !pageErrors.some(e => e.message === message))
            pageErrors.push({ message, id, phase, stack: error?.stack?.slice(0, 1200), ...location });
        if (errors.some(e => e.message === message && e.id === id)) return;
        errors.push({ message, id, phase, stack: error?.stack?.slice(0, 1200), ...location });
        feedback();
    };
    let lastFeedback = '';
    ${imageDiagnostics}
    const feedback = (force = false) => {
        const nodes = Array.from(document.querySelectorAll('[data-unity-visual]')).slice(0, 100);
        const images = Array.from(document.images).filter(img=>img.getAttribute('src') || img.getAttribute('srcset'));
        const imageLayout = images.slice(0,100).map(inspectImageLayout);
        const imageWarnings = imageLayout.filter(img=>img.loaded && !img.hidden && img.clippedBy.length && img.visibleFraction<0.1)
            .map(img=>({id:img.id,phase:'layout',message:img.element+' loaded at '+img.width+' × '+img.height+' px, but only '+img.visibleWidth+' × '+img.visibleHeight+' px remains inside '+img.clippedBy.join(', ')+'. Check image sizing and CSS selectors; keep this clipping only if intentional.'}));
        const imageErrors = images.filter(img=>img.complete && !img.naturalWidth).map(img=>({phase:'resource',
            id:img.closest('[data-unity-visual]')?.getAttribute('data-unity-visual'),
            message:'Image failed to load: '+(img.alt || img.id || 'unnamed image')+'. Inspect its source and asset reference.',
            source:(img.currentSrc || img.getAttribute('src') || '').startsWith('data:')?'embedded image':(img.currentSrc || img.getAttribute('src') || '').slice(0,150)}));
        const assetStates = Array.from(assetImages.values());
        const assetFailures = assetStates.filter(a=>a.error).map(a=>({phase:'resource',source:'unity-asset:'+a.id,message:a.error}));
        const bindings = nodes.map(element => {
            const points = [...element.querySelectorAll('[data-unity-point]')]
                .filter(node => node.closest('[data-unity-visual]') === element).map(node => node.__unityPoint).filter(Boolean);
            const filterableCount = points.filter(point => point.fields.length).length;
            return {visualId:element.getAttribute('data-unity-visual'),count:points.length,filterableCount,
                inspectOnlyCount:points.length-filterableCount};
        });
        const payload = { errors: [...errors,...imageErrors,...assetFailures].slice(0,10), revision, contextKey, theme: document.documentElement.dataset.theme, dataStatus: loaded && !requests.size && !busySlots.length && assetStates.every(a=>a.complete) && images.every(img=>img.complete || img.loading==='lazy') ? 'ready' : 'loading', rendered: nodes.filter(e => mounted.has(e)).map(e => e.getAttribute('data-unity-visual')),
            bindings,
            warnings: imageWarnings.slice(0,20),
            layout: { viewport: { width: innerWidth, height: innerHeight },
                document: { width: document.documentElement.scrollWidth, height: document.documentElement.scrollHeight },
                images: imageLayout,
                visuals: nodes.map(element => {
                    const r = element.getBoundingClientRect();
                    return { id: element.getAttribute('data-unity-visual'), x: Math.round(r.x + scrollX),
                        y: Math.round(r.y + scrollY), width: Math.round(r.width), height: Math.round(r.height) };
                }) } };
        const encoded = JSON.stringify(payload);
        if (force || encoded !== lastFeedback) { lastFeedback = encoded; send('feedback', payload); }
    };
    // Verify each reused image in the actual sandbox, including CSS backgrounds,
    // SVG images, canvas inputs and off-screen/lazy placements.
    Object.entries(assetURLs || {}).forEach(([id,url])=>{
        const image = new Image(), state = {id,image,complete:false,error:null,timer:null};
        const finish = error => { clearTimeout(state.timer); state.complete=true; state.error=error; feedback(); };
        assetImages.set(id,state);
        state.timer=setTimeout(()=>finish('Report image loading timed out. Refresh the page and verify again.'),30000);
        image.onload=()=>finish(null);
        image.onerror=()=>finish('A reused report image could not be decoded in this page. Inspect its asset reference and source.');
        image.src=url;
    });
    window.addEventListener('pagehide',()=>{assetImages.forEach(a=>{clearTimeout(a.timer);a.image.onload=a.image.onerror=null;});assetImages.clear();});
    const known = (value) => value && ((value.kind === 'visual' && visuals.some(v => v.id === value.id)) ||
        (value.kind === 'data' && dataKeys.includes(value.id)));
    const bounds = (element) => {
        if (!element)
            return null;
        const r = element.getBoundingClientRect();
        return { x: r.x, y: r.y, width: r.width, height: r.height };
    };
    const locate = (value) => Array.from(document.querySelectorAll(value?.kind === 'visual' ? '[data-unity-visual]' : '[data-unity-data]'))
        .find(e => e.getAttribute(value?.kind === 'visual' ? 'data-unity-visual' : 'data-unity-data') === value?.id &&
        (value?.column == null || e.getAttribute('data-unity-column') === value.column) &&
        (value?.row == null || Number(e.getAttribute('data-unity-row') || 0) === value.row)) || null;
    const select = (value, element, extend = false) => {
        if (!known(value))
            return;
        if (extend && value.kind === 'visual') {
            const ids = new Set(selection?.kind === 'visual' ? selection.selectedVisualIds || [selection.id] : []);
            if (ids.has(value.id)) ids.delete(value.id); else ids.add(value.id);
            const list = Array.from(ids);
            value = list.length ? { kind: 'visual', id: list[list.length - 1], selectedVisualIds: list } : null;
            element = null;
        }
        selection = value;
        selectedElement = value ? element || locate(value) : null;
        highlight();
        send('selection', { selection: value, rect: bounds(selectedElement) });
    };
    // Optional page-local helper. Nothing writes appearance state until authored
    // HTML opts in; native CSS/classes/media queries remain entirely page-owned.
    const themeMedia = typeof matchMedia === 'function' ? matchMedia('(prefers-color-scheme: dark)') : null;
    const getTheme = () => document.documentElement.dataset.theme === 'dark' ? 'dark' : 'light';
    const setTheme = value => {
        if(!['light','dark','system'].includes(value)) throw new Error('Theme must be light, dark or system.');
        themeChoice=value;
        const theme=value==='system' ? themeMedia?.matches ? 'dark' : 'light' : value;
        document.documentElement.dataset.theme=theme;
        document.documentElement.style.colorScheme=theme;
        themeListeners.forEach(fn=>{try{fn(theme);}catch(error){reportError(error,undefined,'theme');}});
        if(loaded) requestAnimationFrame(()=>feedback());
    };
    const systemThemeChanged = () => { if(themeChoice==='system') setTheme('system'); };
    themeMedia?.addEventListener('change',systemThemeChanged);
    window.addEventListener('pagehide',()=>themeMedia?.removeEventListener('change',systemThemeChanged));
    const activate = point => {
        if (!point.fields.length) return;
        if (point.visualId === interaction?.visualId && point.dataId === interaction?.dataId && JSON.stringify(point.fields) === JSON.stringify(interaction?.fields)) {
            send('clear-interaction');
            return;
        }
        send('interaction',{interaction:{visualId:point.visualId,dataId:point.dataId,fields:point.fields,label:point.label}});
    };
    const request = (visualId,dataId,options) => new Promise((resolve,reject) => {
        const id=++requestId; const timer=setTimeout(()=>{requests.delete(id);refreshChrome();const error=new Error('The query request timed out.');reportError(error,visualId,'query');reject(error);},120000);
        requests.set(id,{resolve,reject,timer,generation,visualId}); refreshChrome(); send('query',{requestId:id,visualId,dataId,options});
        if(loaded) feedback();
    });
    const saveFailure = (code, message) => { const error = new Error(message); error.code = code; return error; };
    const save = (element, text) => {
        const uid = element instanceof Element ? element.getAttribute('data-unity-id') || '' : typeof element === 'string' ? element : '';
        if (typeof text !== 'string') return Promise.reject(saveFailure('failed', 'unity.save needs the element and its text as a string.'));
        const seen = refusals.get(uid) || 0;
        const result = (fieldSaves.get(uid) || Promise.resolve()).then(() => new Promise((resolve, reject) => {
            if ((refusals.get(uid) || 0) !== seen) { reject(saveFailure('changed', 'Someone else just changed this. The page now shows their version.')); return; }
            const id = ++saveId;
            const timer = setTimeout(() => { saves.delete(id); reject(saveFailure('failed', 'Saving did not finish. Check your connection and try again.')); }, 40000);
            saves.set(id, { resolve, reject, timer, uid, text });
            send('save', { requestId: id, uid, text, previousText: appliedText.get(uid)?.text ?? '' });
        }));
        fieldSaves.set(uid, result.catch(() => {}));
        return result;
    };
    const unity = Object.freeze({
        data: (key) => results[key],
        value: (key, column, row = 0) => {
            const result = results[key];
            if (!result || result.error)
                return null;
            return result.records?.[row]?.[column || result.columns?.[0]];
        },
        onData: (callback) => {
            callbacks.push(callback);
            if (loaded)
                callback(results);
            return () => { callbacks = callbacks.filter(c => c !== callback); };
        },
        select,
        canSave: () => Boolean(canSaveAtLoad),
        save,
        navigate: (pageId) => send('navigate', { pageId }),
        filter: () => { throw new Error('Use a registered visual binding with unity.bind / unity.activate for runtime filters. Save authored filters through UpdateHtmlPage.'); },
        theme: Object.freeze({ get:getTheme, set:setTheme,
            onChange:fn=>{themeListeners.add(fn);fn(getTheme());return()=>themeListeners.delete(fn);} }),
        format: (value, options) => {
            if (value == null) return '—';
            // SQL decimals may be exact strings. Text, dates and booleans must
            // not be coerced into numbers just because a table formats a cell.
            const numeric = typeof value === 'number' || typeof value === 'bigint' ||
                (typeof value === 'string' && /^[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:e[+-]?\d+)?$/i.test(value));
            return numeric ? new Intl.NumberFormat(undefined, options).format(value) : String(value);
        },
        formatDate: (value, options) => value == null ? '—' : new Intl.DateTimeFormat(undefined, { timeZone: 'UTC', ...options }).format(
            new Date(typeof value==='string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?$/.test(value) ? value+'Z' : value)),
    });
    window.unity = unity;
    window.addEventListener('error', event => reportError(event.error || event.message, undefined, 'page-script', true,
        {source:event.filename,line:event.lineno,column:event.colno}));
    // Image resource failures do not bubble to the normal JavaScript error handler.
    document.addEventListener('error', event => { if(event.target instanceof HTMLImageElement) feedback(); }, true);
    document.addEventListener('load', event => { if(event.target instanceof HTMLImageElement) feedback(); }, true);
    window.addEventListener('unhandledrejection', event => reportError(event.reason, undefined, 'async', true));
    window.addEventListener('securitypolicyviolation', event => reportError('Browser policy blocked ' + event.effectiveDirective + ': ' + event.blockedURI +
        '. Use inline presentation resources or registered Unity data/assets.', undefined, 'resource', true,
        {source:event.sourceFile,line:event.lineNumber,column:event.columnNumber}));
    ${visualRuntime}
    // Registration also marks chart geometry or composite content for inspection.
    // Only scalar text bindings should replace their authored content.
    const dataBindable = element => !(element.getAttribute('data-unity-bind') === 'none' ||
        (element.namespaceURI === 'http://www.w3.org/2000/svg' && !['text', 'tspan'].includes(element.localName)) ||
        (element.children.length > 0 && element.getAttribute('data-unity-bind') !== 'text'));
    const writeData = (element, visualId, id) => {
        const result=results[visualId+'/'+id] || results[id];
        const column = element.getAttribute('data-unity-column') || result?.columns?.[0];
        const row = Number(element.getAttribute('data-unity-row') || 0);
        const value = result?.records?.[row]?.[column];
        if (result?.error) {
            element.textContent = 'Data unavailable';
            reportError(result.error, id, 'query');
        }
        else {
            const format = element.getAttribute('data-unity-format');
            const options = format === 'currency' ? { style: 'currency', currency: element.getAttribute('data-unity-currency') || 'USD', maximumFractionDigits: 0 } :
                format === 'percent' ? { style: 'percent', maximumFractionDigits: 1 } : { maximumFractionDigits: 2 };
            const numeric = typeof value === 'number' || (typeof value === 'string' && /^-?\d+(\.\d+)?$/.test(value)) ? Number(value) : NaN;
            // SQL decimals travel as exact strings and unity.format keeps their digits. Formatting is an explicit display choice.
            element.textContent = value == null ? '—' : format && Number.isFinite(numeric) ? unity.format(value, options) : String(value);
        }
    };
    // Newly inserted markup (an applied patch) takes the data already loaded; visual mounts are never inside patched markup.
    const bindFresh = root => root.querySelectorAll('[data-unity-data]').forEach(element => {
        const id = element.getAttribute('data-unity-data') || '';
        if (!dataKeys.includes(id)) { reportError('Unregistered data: ' + id); return; }
        if (!dataBindable(element) || !loaded) return;
        writeData(element, element.closest('[data-unity-visual]')?.getAttribute('data-unity-visual'), id);
    });
    function render(changedSlots) {
        const changed = Array.isArray(changedSlots) ? new Set(changedSlots) : null;
        // A visual-specific result overrides the shared dataset. A change to
        // that shared fallback must not rebuild an unchanged source visual.
        const slotChanged = (id, key) => !changed || changed.has(id + '/' + key) ||
            (!results[id + '/' + key] && changed.has(key));
        const candidates = Array.from(document.querySelectorAll('[data-unity-visual]')).filter(element => {
            const definition = visuals.find(v => v.id === element.getAttribute('data-unity-visual'));
            return !changed || definition?.dataIds.some(key => slotChanged(definition.id, key));
        });
        const roots = candidates.filter(element => !candidates.some(parent => parent !== element && parent.contains(element)));
        const affected = element => roots.some(root => root === element || root.contains(element));
        if (!changed) clearChrome();
        else Array.from(chromeNodes.keys()).filter(affected).forEach(removeChrome);
        const refreshedIds = new Set();
        roots.forEach(root => [root, ...root.querySelectorAll('[data-unity-visual]')].forEach(element => refreshedIds.add(element.getAttribute('data-unity-visual'))));
        errors = changed ? errors.filter(error => !refreshedIds.has(error.id)) : [...pageErrors];
        cleanups.forEach((functions, element) => {
            if (!affected(element)) return;
            cleanups.delete(element);
            functions.forEach(fn => { try { fn(); } catch (error) { reportError(error); } });
        });
        let instances = 0;
        const mount = (element, ancestors) => {
            mounted.delete(element);
            const id = element.getAttribute('data-unity-visual');
            const definition = visuals.find(v => v.id === id);
            if (!definition) {
                reportError('Unregistered visual: ' + id);
                return;
            }
            if (ancestors.includes(definition.id) || ++instances > 500) {
                reportError('Recursive visual reference: ' + definition.id, definition.id);
                return;
            }
            element.innerHTML = definition.html || '';
            const scoped = key => results[definition.id + '/' + key] || results[key];
            const failed = definition.dataIds.map((key) => scoped(key)?.error).filter(Boolean);
            if (failed.length) {
                element.textContent = 'Unable to load data: ' + failed.join('; ');
                failed.forEach((message) => reportError(message, definition.id, 'query'));
                return;
            }
            try {
                const data = Object.fromEntries(definition.dataIds.map((key) => [key, scoped(key)]));
                const api = visualAPI(unity,definition,element,data,request,activate,()=>interaction,reportError);
                const disposers = [api.dispose];
                cleanups.set(element, disposers);
                let program;
                try { program = new Function('element', 'data', 'unity', (definition.script || '') + '\n//# sourceURL=unity-visual-' + definition.id + '.js'); }
                catch(error) { reportError(error,definition.id,'syntax'); throw error; }
                const cleanup = program(element, data, api);
                if (typeof cleanup === 'function')
                    disposers.push(cleanup);
                else if(cleanup && typeof cleanup.then==='function') {
                    reportError('Visual scripts must return a cleanup function, not a Promise. Start asynchronous work with a catch handler.',definition.id,'contract');
                    cleanup.catch(error=>reportError(error,definition.id,'async'));
                }
                element.querySelectorAll('[data-unity-data][data-unity-row]').forEach(node=>{
                    if(node.closest('[data-unity-visual]')!==element || node.__unityPoint) return;
                    const key=node.getAttribute('data-unity-data');
                    if(definition.dataIds.includes(key)) api.bind(node,key,Number(node.getAttribute('data-unity-row')));
                });
                Array.from(element.querySelectorAll('[data-unity-visual]'))
                    .filter(child => child.parentElement?.closest('[data-unity-visual]') === element)
                    .forEach(child => mount(child, [...ancestors, definition.id]));
                mounted.add(element);
            }
            catch (error) {
                element.textContent = 'Unable to render this visual.';
                reportError(error, definition.id);
            }
        };
        roots.forEach(element => {
            const ancestors = [];
            for (let parent = element.parentElement?.closest('[data-unity-visual]'); parent; parent = parent.parentElement?.closest('[data-unity-visual]'))
                ancestors.push(parent.getAttribute('data-unity-visual'));
            mount(element, ancestors);
        });
        document.querySelectorAll('[data-unity-data]').forEach(element => {
            const id = element.getAttribute('data-unity-data') || '';
            if (!dataKeys.includes(id)) {
                reportError('Unregistered data: ' + id);
                return;
            }
            if (!dataBindable(element)) return;
            const visualId=element.closest('[data-unity-visual]')?.getAttribute('data-unity-visual');
            if (!affected(element) && !slotChanged(visualId, id)) return;
            writeData(element, visualId, id);
        });
        callbacks.forEach(callback => { try {
            callback(results);
        }
        catch (error) {
            reportError(error);
        } });
        refreshChrome(); requestAnimationFrame(() => feedback());
    }
    // Live viewing: apply the server's net patches to the running document, in place (docs/architecture/live-html-pages.md section 7).
    // Markup is parsed in an inert template, so scripts in a patch never run; the server rebuilds the frame for any script anyway.
    const UID = /^[a-z][a-z0-9]{5}$/;
    const inertFragment = markup => { const holder = document.createElement('template'); holder.innerHTML = markup; return holder.content; };
    const targetsOf = uid => {
        const found = [];
        const visit = root => {
            root.querySelectorAll('[data-unity-id="' + uid + '"]').forEach(node => found.push(node));
            root.querySelectorAll('template').forEach(holder => visit(holder.content));
        };
        visit(document);
        return found;
    };
    const applyPatches = message => {
        const version = message.version;
        const fail = reason => send('apply-failed', { reason: String(reason).slice(0, 200), version });
        const planned = [];
        for (const patch of Array.isArray(message.patches) ? message.patches : []) {
            if (!patch || !UID.test(String(patch.uid)) || !['attrs', 'text', 'children'].includes(patch.op)) return fail('invalid patch');
            const nodes = targetsOf(patch.uid);
            if (!nodes.length) return fail('missing target ' + patch.uid);
            let wanted = null;
            if (patch.op === 'attrs') {
                const parsed = inertFragment(String(patch.startTag)).firstElementChild;
                if (!parsed || nodes.some(node => node.localName !== parsed.localName)) return fail('start tag does not match ' + patch.uid);
                wanted = Array.from(parsed.attributes).filter(attr => attr.name !== 'data-unity-id');
            }
            planned.push({ patch, nodes, wanted });
        }
        try {
            planned.forEach(({ patch, nodes, wanted }) => nodes.forEach(node => {
                if (patch.op === 'text') {
                    const text = String(patch.text);
                    // This frame already knows the field at this version or later (its own save): the patch is older news.
                    if (appliedText.get(patch.uid)?.version >= version) return;
                    if (typing(node) && shown(node) !== text) deferredText.set(node, { text, version });
                    else { deferredText.delete(node); edited.delete(node); learn(patch.uid, text, version); show(node, text); }
                }
                else if (patch.op === 'children') {
                    node.replaceChildren(inertFragment(String(patch.html)));
                    node.querySelectorAll('[data-unity-writable][data-unity-id]').forEach(field => learn(field.getAttribute('data-unity-id'), field.textContent, version));
                    if (node.getRootNode() === document) bindFresh(node);
                } else {
                    const keep = new Set(wanted.map(attr => attr.name));
                    wanted.forEach(attr => { if (node.getAttribute(attr.name) !== attr.value) node.setAttributeNode(attr.cloneNode(true)); });
                    node.getAttributeNames().forEach(name => { if (name !== 'data-unity-id' && !keep.has(name)) node.removeAttribute(name); });
                    if (node.hasAttribute('data-unity-writable') && !appliedText.has(patch.uid)) learn(patch.uid, node.textContent, version);
                }
            }));
        }
        catch (error) { return fail(error?.message || error); }
        revision = version ?? revision;
        refreshChrome();
        send('applied', { version, fromVersion: message.fromVersion });
    };
    window.addEventListener('message', event => {
        if (event.source !== window.parent || event.data?.channel !== 'unity-html' || event.data?.token !== token)
            return;
        if (event.data.type === 'data') {
            const initialData = !loaded;
            revision = event.data.revision ?? revision;
            const nextContext = event.data.contextKey ?? null;
            if (!loaded || nextContext !== contextKey) {
                generation++;
                requests.forEach(r=>{clearTimeout(r.timer);r.reject(new DOMException('The data context changed.','AbortError'));}); requests.clear();
            }
            contextKey = nextContext;
            results = event.data.results || {};
            editing = Boolean(event.data.editing);
            inspecting = event.data.inspecting == null ? editing : Boolean(event.data.inspecting);
            interaction=event.data.interaction || null;
            loaded = true;
            // A new document always needs its first full mount, including
            // visuals with no datasets. Deltas apply only after that mount.
            render(initialData ? undefined : event.data.changedSlots);
        }
        else if (event.data.type === 'loading') {
            busySlots = Array.isArray(event.data.slots) ? event.data.slots : [];
            refreshChrome(); feedback();
        }
        else if (event.data.type === 'interaction-state') {
            interaction=event.data.interaction || null;
            document.querySelectorAll('[data-unity-point]').forEach(node=>{
                const point=node.__unityPoint;
                const active=!!point && point.visualId===interaction?.visualId && point.dataId===interaction?.dataId && JSON.stringify(point.fields)===JSON.stringify(interaction?.fields);
                node.setAttribute(node.localName==='tr'?'aria-selected':'aria-pressed',String(active));node.toggleAttribute('data-unity-selected',active);
            });
            refreshChrome();
        }
        else if (event.data.type === 'mode') {
            editing = Boolean(event.data.editing);
            inspecting = event.data.inspecting == null ? editing : Boolean(event.data.inspecting);
            highlight();
        }
        else if(event.data.type==='query-result') {
            const pending=requests.get(event.data.requestId); if(!pending) return;
            requests.delete(event.data.requestId); clearTimeout(pending.timer); refreshChrome();
            if(event.data.error) pending.reject(new Error(event.data.error)); else {
                errors=errors.filter(e=>e.id!==pending.visualId || e.phase!=='query'); pending.resolve(event.data.result);
            }
            requestAnimationFrame(()=>feedback());
        }
        else if (event.data.type === 'selection') {
            if (JSON.stringify(selection) !== JSON.stringify(event.data.selection)) selectedElement = null;
            selection = known(event.data.selection) ? event.data.selection : null;
            highlight();
        }
        else if (event.data.type === 'inspect' && loaded) { revision = event.data.revision ?? revision; feedback(true); }
        else if (event.data.type === 'apply') applyPatches(event.data);
        else if (event.data.type === 'save-result') {
            const pending = saves.get(event.data.requestId); if (!pending) return;
            saves.delete(event.data.requestId); clearTimeout(pending.timer);
            const field = Array.from(document.querySelectorAll('[data-unity-id]')).filter(node => node.getAttribute('data-unity-id') === pending.uid);
            if (event.data.ok) {
                // A held text from before this save is superseded by it; one from after stays (the next save then answers changed).
                learn(pending.uid, pending.text, event.data.version);
                field.forEach(node => { if (deferredText.get(node)?.version <= event.data.version) deferredText.delete(node); edited.delete(node); });
                pending.resolve({ version: event.data.version });
            }
            else {
                // Someone else changed it first: the field shows their version (a draft held back by typing yields to it).
                if (event.data.code === 'changed') {
                    refusals.set(pending.uid, (refusals.get(pending.uid) || 0) + 1);
                    field.forEach(node => { if (deferredText.has(node)) { showHeld(node); edited.delete(node); } });
                }
                pending.reject(saveFailure(String(event.data.code || 'failed'), String(event.data.message || 'Couldn\'t save.')));
            }
        }
        else if (event.data.type === 'scroll-request') send('scroll', { requestId: event.data.requestId, x: window.scrollX, y: window.scrollY });
        else if (event.data.type === 'scroll-to') {
            window.scrollTo(Number(event.data.x) || 0, Number(event.data.y) || 0);
            send('scrolled', { requestId: event.data.requestId });
        }
    });
    document.addEventListener('input', event => { if (event.target instanceof Element) edited.add(event.target); }, true);
    document.addEventListener('focusout', event => {
        const node = event.target;
        if (node instanceof Element && deferredText.has(node) && !edited.has(node)) showHeld(node);
    }, true);
    document.addEventListener('click', event => {
        if(event.composedPath().some(node=>node instanceof Element && node.hasAttribute('data-unity-feedback'))) return;
        const node=event.target instanceof Element ? event.target.closest('[data-unity-point]') : null;
        if(node?.__unityPoint) {
            const extend = inspecting && (event.ctrlKey || event.metaKey || event.shiftKey);
            if(inspecting) select(node.__unityPoint.selection,node,extend); if(!extend && (!inspecting || editing)) activate(node.__unityPoint);
            return;
        }
        if (!inspecting) return;
        const target = event.target instanceof Element ? event.target.closest('[data-unity-data],[data-unity-visual]') : null;
        if (!target)
            return;
        const kind = target.hasAttribute('data-unity-data') ? 'data' : 'visual';
        select({ kind, id: target.getAttribute(kind === 'data' ? 'data-unity-data' : 'data-unity-visual'),
            ...(kind === 'data' ? { column: target.getAttribute('data-unity-column'), row: Number(target.getAttribute('data-unity-row') || 0) } : {}) }, target, event.ctrlKey || event.metaKey || event.shiftKey);
    }, true);
    // A preview has no app chrome around the page: Esc pressed in the page still leaves the preview.
    document.addEventListener('keydown',event=>{ if(event.key==='Escape' && !editing && !event.defaultPrevented && !event.target?.closest?.('input, textarea, select, [contenteditable="true"]')) send('escape'); });
    document.addEventListener('keydown',event=>{
        if(!['Enter',' '].includes(event.key) || !event.target?.__unityPoint) return;
        event.preventDefault(); const extend = inspecting && (event.ctrlKey || event.metaKey || event.shiftKey);
        if(inspecting) select(event.target.__unityPoint.selection,event.target,extend); if(!extend && (!inspecting || editing)) activate(event.target.__unityPoint);
    });
    document.addEventListener('mousemove', event => {
        if (!inspecting)
            return;
        const element = event.target instanceof Element ? event.target.closest('[data-unity-data],[data-unity-visual]') : null;
        if(hoverElement!==element){hoverElement=element;highlight();}
    }, { passive: true });
    document.addEventListener('mouseleave',()=>{hoverElement=null;highlight();});
    // Observe layout after asynchronous chart work too. Stable pages generate no traffic.
    const feedbackTimer = setInterval(() => { if (loaded) feedback(); }, 1500);
    window.addEventListener('pagehide', () => clearInterval(feedbackTimer));
    const ready = () => {
        send('ready'); refreshChrome();
        const changes = new MutationObserver(refreshChrome);
        changes.observe(document.body,{childList:true,subtree:true});
        window.addEventListener('pagehide',()=>{changes.disconnect();cancelAnimationFrame(chromeFrame);clearChrome();});
    };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', ready);
    else ready();
}
`;

const safeJSON = (value: unknown) => JSON.stringify(value).replace(/</g, '\\u003c').replace(/\u2028/g, '\\u2028').replace(/\u2029/g, '\\u2029');
/** Each save-enabled field's text in the page as served (its HTML or a registered visual's): what its first save expects. */
function fieldTexts(page: Page): Record<string, string> {
  const parser = new DOMParser(), texts: Record<string, string> = {};
  for (const html of [page.html, ...page.visuals.map(visual => visual.html)]) {
    parser.parseFromString(html || '', 'text/html').querySelectorAll('[data-unity-writable][data-unity-id]').forEach(element => {
      const uid = element.getAttribute('data-unity-id') || '';
      if (!(uid in texts)) texts[uid] = element.textContent ?? '';
    });
  }
  return texts;
}
export function buildFrame(page: Page, token: string, assetURLs: Record<string,string> = {}, assetErrors: FrameFeedback['errors'] = [], canSave = false): string {
  const errors = [...assetErrors];
  const resolve = (source: string, id?: string) => {
    assetIds(source).filter(assetId => !assetURLs[assetId]).forEach(assetId => {
      if (!errors.some(e => e.source === 'unity-asset:' + assetId && e.id === id)) errors.push({id,phase:'resource',source:'unity-asset:'+assetId,
        message:'A referenced report image is unavailable. Use InspectFile and ReuseAsset, or ListReportAssets to choose an imported image.'});
    });
    return replaceAssets(source, assetURLs);
  };
  const doc = new DOMParser().parseFromString(resolve(page.html), 'text/html');
  const visuals = page.visuals.map(v=>({...v, html:resolve(v.html,v.id), css:resolve(v.css,v.id), script:resolve(v.script,v.id)}));
  // Generated code cannot replace the host resource policy or the document base URL.
  doc.querySelectorAll('base,meta[http-equiv]').forEach(e => e.remove());
  const policy = doc.createElement('meta');
  policy.httpEquiv = 'Content-Security-Policy';
  policy.content = "default-src 'none'; script-src 'unsafe-inline' 'unsafe-eval' blob:; style-src 'unsafe-inline'; img-src data: blob:; font-src data:; connect-src 'none'; frame-src 'none'; form-action 'none'; base-uri about:";
  // srcdoc otherwise inherits the APP URL: a normal #fragment leaves the report.
  // A document-local base lets the browser own anchors, :target, hashchange,
  // focus, SVG fragments and fragment-only history, including authored handlers.
  const base = doc.createElement('base');
  base.href = 'about:srcdoc';
  const boot = doc.createElement('script');
  boot.textContent = '(' + runtimeSource + ')(' + safeJSON(token) + ',' + safeJSON(visuals) + ',' + safeJSON(page.data.map(d => d.id)) + ',' + safeJSON(errors) + ',' + safeJSON(assetURLs) + ',' + safeJSON(canSave) + ',' + safeJSON(canSave ? fieldTexts(page) : {}) + ');';
  const styles = doc.createElement('style');
  styles.textContent = ':root{--unity-surface:#fff;--unity-ink:#182033;--unity-border:#e5e7eb}:root[data-theme="dark"]{--unity-surface:#111827;--unity-ink:#f1f5f9;--unity-border:#374151}.unity-table{color:var(--unity-ink);background:var(--unity-surface)}.unity-table-toolbar,.unity-table-status{padding:8px;font-size:12px}.unity-table input{font:inherit;padding:7px;color:inherit;background:var(--unity-surface);border:1px solid var(--unity-border);border-radius:6px}' + visuals.map(v => v.css).join('\n');
  doc.head.prepend(policy, base, boot, styles);
  return '<!doctype html>\n' + doc.documentElement.outerHTML;
}
